import React, { lazy, Suspense, useEffect, useState } from 'react';

const LearningProgressChart = lazy(() => import('./LearningProgressChart'));

const HowTo = () => {
  const [openImage, setOpenImage] = useState(null);

  const apiBase = (process.env.REACT_APP_USER_API_URL || '').replace(/\/$/, '');

  const intonationImage = {
    src: `${apiBase}/uploads/screens/muchwarmerthere.png`,
    title: 'How to read the intonation curve',
    subtitle: 'Example: “Wouldn’t it be much warmer there?”',
  };

  const aiImage = {
    src: `${apiBase}/uploads/screens/response_ai.png`,
    title: 'AI feedback as a pointer',
    subtitle:
      'Use marked phonemes and words as suggestions for what to notice next.',
  };

  useEffect(() => {
    if (!openImage) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenImage(null);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [openImage]);

  return (
    <>
      <div
        id="howto"
        className="relative overflow-hidden rounded-[38px] border border-slate-200 bg-white font-sans text-slate-950 shadow-2xl shadow-slate-200/70"
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.09),transparent_34%),radial-gradient(circle_at_90%_10%,rgba(16,185,129,0.08),transparent_28%)]" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(15,23,42,0.035)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.03)_1px,transparent_1px)] bg-[size:44px_44px] opacity-35" />

        <div className="relative px-5 py-7 md:px-10 md:py-10">
          {/* Header */}
          <header className="mx-auto max-w-5xl">
            <div className="max-w-3xl">
              <SectionLabel tone="blue">How to practise</SectionLabel>

              <h1 className="mt-5 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl md:text-5xl">
                Practise with human-read model phrases.
              </h1>
            </div>
          </header>

          {/* Simple session */}
          <section className="mx-auto mt-10 max-w-5xl rounded-[32px] border border-slate-200 bg-white/90 p-5 shadow-sm backdrop-blur md:p-8">
            <div className="grid gap-8 md:grid-cols-[0.9fr_1.1fr] md:items-start">
              <div>
                <SectionLabel>Basic routine</SectionLabel>

                <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                  A simple practice session
                </h2>
              </div>

              <ol className="space-y-5">
                <PracticeStep
                  number="1"
                  title="Start from a model phrase"
                  text="Use material from a course, audiobook, or dialogue."
                />

                <PracticeStep
                  number="2"
                  title="Echo or shadow"
                  text="Repeat after the model, or speak together with it."
                />

                <PracticeStep
                  number="3"
                  title="Compare and repeat"
                  text="Listen to the model and your recording, then repeat the phrase to improve."
                />
              </ol>
            </div>
          </section>

          {/* Practice time */}
          <section className="mx-auto mt-6 max-w-5xl rounded-[32px] border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50/70 p-5 shadow-sm md:p-8">
            <div className="grid gap-7 md:grid-cols-[0.9fr_1.1fr] md:items-start">
              <div>
                <SectionLabel tone="green">Practice over time</SectionLabel>

                <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                  Regular practice
                </h2>

                <div className="mt-4 inline-flex w-fit rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
                  Start with 5–10 minutes
                </div>
              </div>

              <div className="space-y-4 text-base font-normal leading-8 text-slate-700 md:text-[17px]">
                <p>
                  Pronunciation is physical and cognitive practice: listening,
                  timing, motor skills, memory, and attention all work together.
                  We recommend regular sessions, starting with 5–10 minutes and
                  adjusting the duration to what feels useful for the learner.
                </p>

                <p>
                  A small number of phrases is usually enough for careful
                  repetition. The aim is not to push hard in one session, but to
                  return regularly to exercise.
                </p>

                <p>
                  One of the first changes may be perception. Learners may begin
                  to hear not only separate words, but connected word chains
                  joined by rhythm, intonation, and meaning.
                </p>

                <p>
                  When these patterns become easier to notice, speech also
                  becomes easier to process. Useful phrases and sentence patterns
                  can then be absorbed and reused more naturally, instead of
                  building everything word by word.
                </p>
              </div>
            </div>
          </section>

          {/* Deeper practice */}
          <details className="group mx-auto mt-6 max-w-5xl overflow-hidden rounded-[32px] border border-slate-200 bg-white/95 shadow-sm backdrop-blur">
            <summary className="flex cursor-pointer select-none items-center justify-between gap-4 px-5 py-5 md:px-8">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Optional notes
                </div>

                <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-950 md:text-2xl">
                  A little deeper into pronunciation practice
                </h2>
              </div>

              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-xl font-semibold text-slate-500 transition group-open:rotate-45">
                +
              </span>
            </summary>

            <div className="border-t border-slate-100 px-5 py-6 md:px-8 md:py-8">
              {/* Section 1 — Visual cues */}
              <section className="rounded-[30px] border border-slate-200 bg-slate-50/80 p-5 md:p-7">
                <div className="grid gap-6 md:grid-cols-[0.82fr_1.18fr] md:items-start">
                  <div>
                    <SectionLabel>Visual cues</SectionLabel>

                    <h3 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                      Visual cues that help to follow the model
                    </h3>

                    <p className="mt-4 text-base font-normal leading-8 text-slate-700 md:text-[17px]">
                      The pitch curve shows where the voice rises or falls, and
                      which word carries the main stress.
                    </p>
                  </div>

                  <ImagePreviewButton
                    image={intonationImage}
                    onClick={() => setOpenImage(intonationImage)}
                    label="“Wouldn’t it be much” = one movement"
                    text="These words form one intonation group. The pitch flows across them instead of restarting at each word."
                  />
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <InfoCard title="Movement is not tied to word boundaries">
                    Pitch can continue across several words, or change inside
                    one word. In this example, the question rises toward the
                    end: <span className="font-semibold text-slate-950">there?</span>
                  </InfoCard>

                  <InfoCard title="Gaps can show breaks in voicing">
                    A gap means there is no measurable pitch. This can happen
                    during unvoiced sounds, but also during tiny pauses. If your
                    curve has many more gaps than the model, try a smoother
                    carry-through across words.
                  </InfoCard>
                </div>
              </section>

              {/* Section 2 — Practice modes */}
              <section className="mt-8 rounded-[30px] border border-blue-100 bg-blue-50/55 p-5 md:p-7">
                <div>
                  <SectionLabel tone="blue">Practice modes</SectionLabel>

                  <h3 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                    Two ways to practise
                  </h3>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <ModeCard mode="Mode 1" title="Follow the intonation">
                    This is especially useful in intonation courses such as{' '}
                    <a
                      href="https://www.catchphraser.com/course/flyt-og-rytme"
                      className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-900"
                    >
                      Flyt og rytme
                    </a>{' '}
                    or{' '}
                    <a
                      href="https://www.catchphraser.com/course/intonation-of-colloquial-english"
                      className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-900"
                    >
                      Intonation of Colloquial English
                    </a>
                    . Listen to the model and notice how words are connected,
                    where the voice rises or falls, and which words carry the
                    main stress.
                  </ModeCard>

                  <ModeCard mode="Mode 2" title="Use AI feedback as a pointer">
                    AI feedback may point to phonemes or words that need
                    attention. Do not treat it as a final judgement. Use it as a
                    suggestion for what to focus on next.

                    <ScoreLegend />
                  </ModeCard>
                </div>

                <ImagePreviewButton
                  image={aiImage}
                  onClick={() => setOpenImage(aiImage)}
                  label="Use feedback to choose one focus"
                  text="Look at the marked phonemes or words, choose one focus, and try the phrase again. The goal is not to fix everything at once."
                  tone="blue"
                />

                {/* Spaced repetition */}
                <div className="mt-6 overflow-hidden rounded-[30px] border border-blue-100 bg-white shadow-sm">
                  <div className="grid gap-6 p-5 md:grid-cols-[0.86fr_1.14fr] md:p-7">
                    <div>
                      <SectionLabel tone="blue">Spaced repetition</SectionLabel>

                      <h3 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                        Sounds improve, fade, and improve again
                      </h3>

                      <div className="mt-4 inline-flex w-fit rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                        Normal process
                      </div>
                    </div>

                    <div className="space-y-4 text-base font-normal leading-8 text-slate-700 md:text-[17px]">
                      <p>
                        A sound may go well today, then partly return as a
                        problem later when attention moves elsewhere. This is
                        normal for cognitive and motor skills. Pronunciation
                        skills become more stable through repeated short
                        returns.
                      </p>

                      <p>
                        Do not overwork red or yellow sounds. Notice the
                        problem, try again, and meet the same sound later in
                        another phrase. Small returns are part of the method,
                        not a failure.
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-blue-100 bg-slate-50/70 p-3 md:p-5">
                    <div className="overflow-hidden rounded-2xl border border-blue-100 bg-white">
                      <Suspense
                        fallback={
                          <div className="flex h-64 items-center justify-center text-sm font-medium text-slate-500">
                            Loading learning curve…
                          </div>
                        }
                      >
                        <LearningProgressChart />
                      </Suspense>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </details>
        </div>
      </div>

      {openImage && (
        <ImageOverlay image={openImage} onClose={() => setOpenImage(null)} />
      )}
    </>
  );
};

const SectionLabel = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'border-slate-200 bg-white text-slate-600',
    blue: 'border-blue-200 bg-white text-blue-700',
    green: 'border-emerald-200 bg-white text-emerald-700',
  };

  return (
    <div
      className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${tones[tone]}`}
    >
      {children}
    </div>
  );
};

const PracticeStep = ({ number, title, text }) => {
  return (
    <li className="group flex gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-sm font-semibold text-blue-700 ring-1 ring-blue-100">
        {number}
      </div>

      <div>
        <h3 className="text-base font-semibold text-slate-950 md:text-lg">
          {title}
        </h3>

        <p className="mt-1 text-base font-normal leading-7 text-slate-600">
          {text}
        </p>
      </div>
    </li>
  );
};

const InfoCard = ({ title, children }) => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-base font-semibold text-slate-950 md:text-lg">
        {title}
      </h3>

      <p className="mt-2 text-base font-normal leading-8 text-slate-700 md:text-[17px]">
        {children}
      </p>
    </div>
  );
};

const ModeCard = ({ mode, title, children }) => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
      <div className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-700">
        {mode}
      </div>

      <h4 className="mt-2 text-lg font-semibold text-slate-950 md:text-xl">
        {title}
      </h4>

      <div className="mt-3 text-base font-normal leading-8 text-slate-700 md:text-[17px]">
        {children}
      </div>
    </div>
  );
};

const ScoreLegend = () => {
  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-sm font-semibold text-slate-950">
        What the colours mean
      </div>

      <div className="mt-3 space-y-2 text-sm font-normal leading-6 text-slate-700">
        <LegendRow
          colourClass="bg-green-500"
          label="Green"
          range="80–100"
          text="usually good enough to move on"
        />

        <LegendRow
          colourClass="bg-yellow-400"
          label="Yellow"
          range="40–80"
          text="notice it, but do not panic"
        />

        <LegendRow
          colourClass="bg-red-500"
          label="Red"
          range="below 40"
          text="try again, but do not overwork it"
        />
      </div>
    </div>
  );
};

const LegendRow = ({ colourClass, label, range, text }) => {
  return (
    <div className="flex gap-3">
      <span
        className={`mt-1.5 h-3 w-3 shrink-0 rounded-full ${colourClass}`}
        aria-hidden="true"
      />

      <div>
        <span className="font-semibold text-slate-950">{label}</span>{' '}
        <span className="font-semibold text-slate-700">({range})</span>: {text}.
      </div>
    </div>
  );
};

const ImagePreviewButton = ({ image, onClick, label, text, tone = 'slate' }) => {
  const isBlue = tone === 'blue';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group mt-6 block w-full overflow-hidden rounded-[28px] border bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        isBlue ? 'border-blue-100' : 'border-slate-200'
      }`}
    >
      <div className="overflow-hidden bg-white">
        <img
          src={image.src}
          alt={image.title}
          className="w-full transition duration-300 group-hover:scale-[1.015]"
        />
      </div>

      <div className="p-5 md:p-6">
        <div className="text-base font-semibold text-slate-950 md:text-lg">
          {label}
        </div>

        <p className="mt-2 text-base font-normal leading-7 text-slate-700">
          {text}
        </p>

        <div className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
          Click to enlarge
        </div>
      </div>
    </button>
  );
};

const ImageOverlay = ({ image, onClose }) => {
  return (
    <div className="fixed inset-0 z-[250]">
      <button
        type="button"
        aria-label="Close image preview"
        onClick={onClose}
        className="absolute inset-0 bg-black/45"
      />

      <div className="absolute inset-0 flex items-center justify-center p-3 md:p-6">
        <div
          className="relative flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-4 py-3 md:px-5">
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-slate-950 md:text-xl">
                {image.title}
              </h2>

              {image.subtitle && (
                <p className="mt-1 truncate text-sm font-normal text-slate-600 md:text-base">
                  {image.subtitle}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Close
            </button>
          </div>

          <div className="overflow-auto bg-slate-50 p-3 md:p-5">
            <img
              src={image.src}
              alt={image.title}
              className="mx-auto h-auto max-h-[76vh] w-auto max-w-full rounded-2xl border border-slate-200 bg-white shadow-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowTo;